import React, { useRef, useMemo, useEffect, useState, type ChangeEvent } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  useDisclosure,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Badge,
  Tabs,
  TabList,
  Tab,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { CreateTicketModal } from '@/components/client/CreateTicketModal'
import { useToastNotification } from '@/lib/utils/useToastNotification' 
import apiClient from '@/lib/api/client'
import { useAccessCheck } from '@/hooks/useAccess'
import { Search } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useThemeColors } from '@/hooks/useThemeColors'

export default function MyRequestsPage() {
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { showSuccess, showError } = useToastNotification()
  const access = useAccessCheck('/my-request')
  const navigate = useNavigate()
  const colors = useThemeColors()

  const [serviceOptions, setServiceOptions] = useState<{ label: string; value: string }[]>([])
  const [serviceParentOptions, setServiceParentOptions] = useState<{ label: string; value: string }[]>([])
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRequests, setTotalRequests] = useState(0)
  const [activeTab, setActiveTab] = useState<'all' | 'trash'>('all')
  const [totalTrash, setTotalTrash] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingTicket, setEditingTicket] = useState<any>(null)

  useEffect(() => {
    // Fetch all service types (both parents and children) for the CreateTicketModal
    apiClient.get('/options/service-types')
      .then((res) => {
        const allTypes = res.data || []
        // Filter parent types (where parent_id is null)
        const parents = allTypes
          .filter((s: any) => !s.parent_id)
          .map((s: any) => ({ label: s.name, value: String(s.id) }))
        // Filter child types (where parent_id is not null) - these are the actual selectable types
        const children = allTypes
          .filter((s: any) => s.parent_id)
          .map((s: any) => ({ 
            label: s.name, 
            value: String(s.id), 
            parent_id: String(s.parent_id) // Keep as string for comparison
          }))
        setServiceParentOptions(parents)
        setServiceOptions(children)
      })
      .catch(() => {})

    apiClient.get('/support/my-request/categories')
      .then((res) => {
        const list = res.data?.data ?? res.data ?? []
        const mapped = (Array.isArray(list) ? list : []).map((c: any) => ({ label: c.name ?? c.label ?? '', value: String(c.id ?? c.value ?? '') }))
        setCategoryOptions(mapped)
      })
      .catch(() => {})
  }, [])

  // When modal opens for edit, fetch ticket to prefill form
  useEffect(() => {
    if (!isOpen || editingId == null) {
      if (!isOpen) setEditingTicket(null)
      return
    }
    apiClient.get(`/support/my-request/${editingId}`)
      .then((res) => {
        const data = res.data?.data ?? res.data
        setEditingTicket(data ? { ...data, id: data.id ?? editingId } : null)
      })
      .catch(() => setEditingTicket(null))
  }, [isOpen, editingId])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRequests(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const q = searchRef.current?.value || ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const tableOptions: DataTableOptions = useMemo(
    () => ({
    selectable: false,
    dataFields: {
      request_number: { name: 'Request #', withSort: true, maxWidth: '160px' },
      ticket_id: { name: 'Parent Ticket', maxWidth: '160px' },
      service_type: { name: 'Service', maxWidth: '160px' },
      category: { name: 'Category', maxWidth: '200px' },
      description: { name: 'Issue', maxWidth: '340px' },
      ticket_status: {
        name: 'Status',
        withFilter: true,
        filterOptions: [
          { value: '1', label: 'New' },
          { value: '2', label: 'Pending' },
          { value: '3', label: 'In Progress' },
          { value: '4', label: 'Resolved' },
          { value: '5', label: 'Closed' },
          { value: '6', label: 'Cancelled' },
          { value: '0', label: 'Draft' },
        ],
        // Map numeric ticket_status values to badge colors
        badge: {
          '0': 'gray',
          '1': 'blue',
          '2': 'yellow',
          '3': 'orange',
          '4': 'green',
          '5': 'gray',
          '6': 'red',
        },
        badgeLabels: {
          '0': 'Draft',
          '1': 'New',
          '2': 'Pending',
          '3': 'In Progress',
          '4': 'Resolved',
          '5': 'Closed',
          '6': 'Cancelled',
        },
        maxWidth: '140px',
      },
      created_at: { name: 'Date Created', withSort: true, maxWidth: '180px' },
      last_updated_at_human: { name: 'Last Updated', maxWidth: '120px' },
    },
    dataSource: activeTab === 'trash' ? '/support/my-request/archived' : '/support/my-request',
    primaryKey: 'id',
    softDelete: true,
    displayInModal: true,
    noRecordText: 'No requests found',
    canEditRow: (row) => !row.submitted_at,
  }),
    [activeTab]
  )

  const customRenderers = useMemo(
    () => ({
      created_at: (value: string | null | undefined) => {
        if (!value) return ''
        try {
          const date = typeof value === 'string' ? parseISO(value) : new Date(value)
          return format(date, 'MMM d, yyyy · h:mm a')
        } catch {
          return String(value)
        }
      },
    }),
    []
  )

  const handleCreateSubmit = async (payload: any) => {
    try {
      let attachment_metadata: any[] = []

      // Include existing attachments that weren't removed
      if (Array.isArray(payload.existingAttachments) && payload.existingAttachments.length > 0) {
        attachment_metadata = payload.existingAttachments.map((att: any) => ({
          file_name: att.file_name,
          path: att.file_url || att.path
        }))
      }

      // Upload new attachments and add them to metadata
      if (Array.isArray(payload.attachments) && payload.attachments.length > 0) {
        const uploadForm = new FormData()
        // MediaController expects 'files' field (Laravel handles both 'files' and 'files[]')
        payload.attachments.forEach((f: File) => uploadForm.append('files[]', f))

        const uploadRes = await apiClient.post('/files/upload-multiple', uploadForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (uploadRes?.data?.success && uploadRes?.data?.data) {
          // MediaController returns MediaResource collection/object
          // MediaResource has: file_name, file_url, file_type, file_size, etc.
          const uploadedFiles = Array.isArray(uploadRes.data.data) 
            ? uploadRes.data.data 
            : [uploadRes.data.data]
          
          const newAttachments = uploadedFiles.map((r: any) => ({ 
            file_name: r.file_name || r.filename || r.file_name, 
            path: r.file_url || r.path 
          }))
          
          // Merge with existing attachments
          attachment_metadata = [...attachment_metadata, ...newAttachments]
        }
      }

      const preferred_schedule_at = payload.dispatchDate && payload.dispatchTime
        ? `${payload.dispatchDate} ${payload.dispatchTime}:00`
        : null

      const preferred_contact_at = payload.preferredContactDate && payload.preferredContactTime
        ? `${payload.preferredContactDate} ${payload.preferredContactTime}:00`
        : null

      const body = {
        service_type_id: Number(payload.service),
        category_ids: (payload.category_ids || []).map(Number).filter(Boolean),
        description: payload.description,
        requested_quantity: Number(payload.devicesAffected || 1),
        preferred_schedule_at,
        attachment_metadata,
        contact_number: payload.contactNumber,
        contact_name: payload.contactPerson,
        contact_email: payload.email,
        preferred_contact_at,
        request_source: 'web',
        request_channel: 'portal',
        submit_request: Boolean(payload.submitRequest),
      }

      if (editingId != null) {
        await apiClient.put(`/support/my-request/web/${editingId}`, body)
        showSuccess(
          payload.submitRequest ? 'Request submitted' : 'Draft saved',
          payload.submitRequest ? 'Your request has been submitted.' : 'Your draft has been saved.'
        )
        setEditingId(null)
        setEditingTicket(null)
        onClose()
      } else {
        await apiClient.post('/support/my-request/web', body)
        showSuccess(
          payload.submitRequest ? 'Ticket created' : 'Draft saved',
          payload.submitRequest ? 'Your request has been submitted.' : 'Your draft has been saved.'
        )
      }

      tableRef.current?.reload()
    } catch (err) {
      showError(err)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                My Requests
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                View and manage your support requests
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRequests}
                        </Badge>
                      </Tab>
                      <Tab>
                        Trash{' '}
                        <Badge ml={2} colorScheme="red">
                          {totalTrash}
                        </Badge>
                      </Tab>
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons createLabel="Create Request" onCreate={onOpen} pathname="/my-request" />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request number, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  customRenderers={customRenderers}
                  access={{
                    can_create: access.can_create,
                    can_edit: access.can_edit,
                    can_delete: access.can_delete,
                    can_view: access.can_view,
                    can_export: access.can_export,
                    can_import: access.can_import,
                  }}
                  onOpenModal={(id: any) => { setEditingId(id); onOpen(); }}
                  setSubSub={showSubSubCallback}
                  params={tableParams}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <CreateTicketModal
        isOpen={isOpen}
        onClose={() => { onClose(); setEditingId(null); setEditingTicket(null); }}
        onSubmit={handleCreateSubmit}
        serviceOptions={serviceOptions}
        serviceParentOptions={serviceParentOptions}
        categoryOptions={categoryOptions}
        initialData={editingTicket}
        editingId={editingId}
        isLoadingEdit={editingId != null && editingTicket == null}
      />
    </AdminLayout>
  )
}
